import React, { useEffect, useMemo, useState } from 'react';
import type { ShoppingListItem } from '@/types/shoppingList';
import { Button, Input, BodyText, Card, LoadingSpinner } from '@/components/ui';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';

const TrashIcon: React.FC = () => (
  <svg
    className="w-4 h-4"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M19 6l-1 14H6L5 6m3 0V4h8v2m-9 0h10M10 10v6m4-6v6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface ShoppingListTableProps {
  items: ShoppingListItem[];
  onToggleItem: (itemId: string, checked: boolean) => void | Promise<void>;
  onQuantityChange: (itemId: string, quantity: number) => void | Promise<void>;
  onDeleteItem?: (itemId: string) => void | Promise<void>;
  disabled?: boolean;
  isLoading?: boolean;
}

const formatQuantity = (value: number): string => {
  if (Number.isInteger(value)) {
    return value.toString();
  }
  return value.toFixed(2).replace(/\.?0+$/, '');
};

export const ShoppingListTable: React.FC<ShoppingListTableProps> = ({
  items,
  onToggleItem,
  onQuantityChange,
  onDeleteItem,
  disabled = false,
  isLoading = false,
}) => {
  const { isDark } = useTheme();
  const [quantities, setQuantities] = useState<Record<string, string>>({});

  useEffect(() => {
    const nextQuantities = items.reduce<Record<string, string>>((acc, item) => {
      acc[item.id] = formatQuantity(item.quantity);
      return acc;
    }, {});
    setQuantities(nextQuantities);
  }, [items]);

  const handleQuantityBlur = async (itemId: string) => {
    const parsed = parseFloat(quantities[itemId]);
    if (Number.isNaN(parsed)) {
      // Reset to previous valid number
      const item = items.find(i => i.id === itemId);
      if (item) {
        setQuantities(prev => ({ ...prev, [itemId]: formatQuantity(item.quantity) }));
      }
      return;
    }
    await onQuantityChange(itemId, parsed);
    // Reformat value after save to clear active/edit state
    setQuantities(prev => ({ ...prev, [itemId]: formatQuantity(parsed) }));
  };

  const hasItems = useMemo(() => items.length > 0, [items.length]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingSpinner
          message="Loading your shopping list..."
          spinnerSize="sm"
          fullScreen={false}
          className="py-10"
        />
      );
    }

    if (!hasItems) {
      return (
        <div className="text-center py-10">
          <BodyText variant="muted">Your shopping list is empty.</BodyText>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr
              className={cn(
                'text-left text-sm',
                isDark ? 'text-secondary-400' : 'text-secondary-600'
              )}
            >
              <th className="px-4 py-3">Item</th>
              <th className="px-4 py-3">Quantity</th>
              <th className="px-4 py-3">Unit</th>
              {onDeleteItem && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map(item => (
              <tr key={item.id} className="text-sm">
                <td className={cn('px-4 py-3')}>
                  <label className="inline-flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={item.isChecked}
                      onChange={e => onToggleItem(item.id, e.target.checked)}
                      disabled={disabled}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      aria-label={`Mark ${item.name} as ${item.isChecked ? 'not completed' : 'completed'}`}
                    />
                    <span className={cn(item.isChecked ? 'line-through text-gray-400' : '')}>{item.name}</span>
                  </label>
                </td>
                <td className="px-4 py-3 w-32">
                  <Input
                    type="number"
                    step="0.5"
                    min={0}
                    value={quantities[item.id] ?? ''}
                    onChange={e => setQuantities(prev => ({ ...prev, [item.id]: e.target.value }))}
                    onBlur={() => void handleQuantityBlur(item.id)}
                    disabled={disabled}
                    className="ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 focus:outline-none focus:border-secondary-300 focus:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    aria-label={`Quantity for ${item.name}`}
                  />
                </td>
                <td className="px-4 py-3 capitalize">{item.unit}</td>
                {onDeleteItem && (
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50 focus:ring-red-500"
                      leftIcon={<TrashIcon />}
                      onClick={() => onDeleteItem(item.id)}
                      disabled={disabled}
                    >
                      Remove
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return <Card className="p-4">{renderContent()}</Card>;
};

export default ShoppingListTable;
